import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Bug, Loader2, Trash2, CheckCircle2, RotateCcw, Image as ImageIcon, Copy, ChevronDown, Moon } from 'lucide-react';
import { Button } from '../ui/button';

export function BugReports() {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewImg, setViewImg] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [minOpen, setMinOpen] = useState(3);
  const [bundling, setBundling] = useState(false);

  const load = () => {
    api.listBugReports()
      .then((res) => setBugs(res.data))
      .catch(() => toast.error('Failed to load bug reports'))
      .finally(() => setLoading(false));
    api.getBugBundle().then((r) => { setBundle(r.data.bundle); setMinOpen(r.data.min_open || 3); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const copyBundle = async () => {
    if (!bundle) return;
    try {
      await navigator.clipboard.writeText(bundle.text);
      try { await api.bugBundleCopied(bundle.id); } catch { /* reports stay open — copy still worked */ }
      toast.success(`Bündel mit ${bundle.count} Report${bundle.count === 1 ? '' : 's'} kopiert — jetzt in den Emergent-Chat einfügen`);
      load();
    } catch { toast.error('Clipboard blockiert — Text manuell markieren & kopieren'); }
  };

  const runBundleNow = async () => {
    setBundling(true);
    try {
      const r = await api.runBugBundleNow();
      if (r.data.ok) toast.success(`Bündel erstellt: ${r.data.count} Reports (${r.data.urgent} dringend) — Push + E-Mail an die Admins`);
      else if (r.data.skipped === 'threshold') toast.info(`Kein Bündel: ${r.data.open} offene Reports, keiner dringend — nötig sind ≥1 „!“ oder ≥${r.data.min_open}`);
      else toast.info('Heute bereits gelaufen');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Lauf fehlgeschlagen'); }
    setBundling(false);
  };

  const setStatus = async (id, status) => {
    try { await api.updateBugReport(id, { status }); load(); } catch { toast.error('Failed to update'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this bug report permanently?')) return;
    try { await api.deleteBugReport(id); toast.success('Bug report deleted'); load(); } catch { toast.error('Failed to delete'); }
  };

  const openCount = bugs.filter((b) => b.status === 'open').length;

  const renderBug = (b) => (
    <div key={b.id} data-testid={`bug-report-${b.id}`}
      className={`border rounded-sm p-3 ${b.status === 'resolved' ? 'border-border/50 opacity-60' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-foreground whitespace-pre-wrap break-words">{b.description}</p>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[10px] text-muted-foreground">
            <span className="font-medium text-foreground/70">{b.reporter_name || b.reporter_email}</span>
            <span>·</span>
            <span>{new Date(b.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            <span className={`px-1.5 py-0 rounded-sm font-mono uppercase ${b.status === 'resolved' ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-400'}`}>{b.status}</span>
            {b.urgent && (
              <span className="px-1.5 py-0 rounded-sm font-mono uppercase bg-red-500/15 text-red-400 border border-red-500/30" data-testid={`bug-urgent-${b.id}`}
                title="Mit „!“ gemeldet — wird in der nächsten Nacht gebündelt, auch wenn weniger als 3 Reports offen sind">! dringend</span>
            )}
            {b.page_url && <span className="text-muted-foreground/50 truncate max-w-[200px]">{b.page_url}</span>}
            {b.device && <span className="text-muted-foreground/60" data-testid={`bug-device-${b.id}`}>{b.device.standalone ? 'PWA' : 'Browser'} · {b.device.viewport}</span>}
          </div>
          {(b.client_log || []).length > 0 && (
            <details className="mt-1.5" data-testid={`bug-client-log-${b.id}`}>
              <summary className="text-[11px] text-muted-foreground cursor-pointer">Geräte-Log ({b.client_log.length} Einträge)</summary>
              <pre className="mt-1 text-[10px] font-mono text-muted-foreground whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                {b.client_log.map(e => `${(e.t || '').slice(11, 19)} ${e.kind}${e.msg ? ` „${e.msg}“` : ''}${e.nav_type ? ` (${e.nav_type})` : ''}${e.path ? ` ${e.path}` : ''}${e.status ? ` ${e.status}` : ''}`).join('\n')}
              </pre>
            </details>
          )}
          {b.has_screenshot && (
            <button onClick={() => setViewImg(api.bugScreenshotUrl(b.id))} data-testid={`bug-screenshot-link-${b.id}`}
              className="mt-2 flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300">
              <ImageIcon className="w-3.5 h-3.5" /> View screenshot
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {b.status === 'open' ? (
            <Button size="sm" variant="ghost" onClick={() => setStatus(b.id, 'resolved')} title="Mark resolved"
              className="h-7 w-7 p-0 text-green-400 hover:text-green-300" data-testid={`resolve-bug-${b.id}`}>
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setStatus(b.id, 'open')} title="Reopen"
              className="h-7 w-7 p-0 text-amber-400 hover:text-amber-300">
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => remove(b.id)} title="Delete"
            className="h-7 w-7 p-0 text-red-400 hover:text-red-300" data-testid={`delete-bug-${b.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const copyForAI = async () => {
    const open = bugs.filter((b) => b.status === 'open');
    if (!open.length) { toast.info('No open bug reports to copy'); return; }
    const text = [
      `Bug Reports aus der App (${open.length} offen, Stand ${new Date().toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}):`,
      ...open.map((b, i) => {
        const when = new Date(b.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
        const dev = b.device ? `\n   Gerät: ${b.device.standalone ? 'PWA (Home-Screen)' : 'Browser'} · ${b.device.viewport || ''} · ${b.device.ua || ''}` : '';
        const log = (b.client_log || []).length
          ? `\n   Log: ` + b.client_log.map(e => `${(e.t || '').slice(11, 19)} ${e.kind}${e.msg ? ` „${e.msg}“` : ''}${e.nav_type ? ` (${e.nav_type})` : ''}${e.path ? ` ${e.path}` : ''}${e.status ? ` ${e.status}` : ''}`).join(' | ')
          : '';
        return `\n${i + 1}. [${b.reporter_name || b.reporter_email} — ${when}${b.page_url ? ` — Seite: ${b.page_url}` : ''}]\n${b.description}${dev}${log}`;
      }),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      // Copied = handed over to the agent → auto-resolve so the list only shows new reports
      try {
        await api.resolveBugsBulk(open.map((b) => b.id));
        load();
        toast.success(`${open.length} report${open.length === 1 ? '' : 's'} copied & marked resolved — paste into the Emergent chat`);
      } catch {
        toast.success(`${open.length} open report${open.length === 1 ? '' : 's'} copied — paste into the Emergent chat`);
      }
    } catch {
      toast.error('Clipboard blocked — select & copy manually');
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="bug-reports-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
          <Bug className="w-4 h-4 text-red-400" /> Bug Reports
          {openCount > 0 && (
            <span className="text-[10px] bg-red-500/15 text-red-400 border border-red-500/30 rounded-full px-2 py-0.5 font-mono">{openCount} open</span>
          )}
        </h3>
        {openCount > 0 && (
          <Button size="sm" variant="outline" onClick={copyForAI} data-testid="copy-bugs-for-ai-btn"
            title="Copies all open reports as text — paste into the Emergent chat so the AI can fix them"
            className="h-7 text-[11px] border-blue-600/40 text-blue-400 hover:bg-blue-600/10 rounded-sm px-2.5">
            <Copy className="w-3 h-3 mr-1.5" /> Copy for AI
          </Button>
        )}
      </div>
      {bundle && bundle.open_count > 0 && (
        <div className="mb-4 border border-red-500/40 bg-red-500/10 rounded-sm p-3 flex items-start gap-3 flex-wrap" data-testid="bug-bundle-banner">
          <Moon className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-medium text-red-200" data-testid="bug-bundle-title">
              Nacht-Bündel vom {new Date(bundle.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} bereit — {bundle.count} Report{bundle.count === 1 ? '' : 's'}{bundle.urgent_count ? ` (${bundle.urgent_count} dringend)` : ''}
            </p>
            <p className="text-[11px] text-red-200/80 mt-0.5">
              Kopieren und in den Emergent-Chat einfügen — die KI löst die Bugs dann sofort. Die Reports gelten danach als übergeben.
              {bundle.email_sent ? ' Der Text liegt auch als E-Mail im Postfach.' : ''}
            </p>
          </div>
          <Button size="sm" onClick={copyBundle} data-testid="bug-bundle-copy-btn"
            className="h-7 text-[11px] bg-red-600 hover:bg-red-500 text-white rounded-sm px-2.5 shrink-0">
            <Copy className="w-3 h-3 mr-1.5" /> Kopieren & in den Chat einfügen
          </Button>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mb-3 flex items-center gap-2 flex-wrap" data-testid="bug-bundle-rule">
        <Moon className="w-3 h-3 shrink-0" />
        <span>Nächtlicher Lauf 00:00 Uhr: Bündel entsteht bei ≥1 Report mit „!“ am Anfang (dringend) oder ab {minOpen} offenen Reports — Push + E-Mail an die Admins.</span>
        <button onClick={runBundleNow} disabled={bundling || openCount === 0} data-testid="bug-bundle-run-now-btn"
          className="text-blue-400 hover:text-blue-300 disabled:opacity-40 underline underline-offset-2">
          {bundling ? 'läuft…' : 'jetzt prüfen'}
        </button>
      </p>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      ) : bugs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No bug reports yet.</p>
      ) : (
        <div className="space-y-2">
          {bugs.filter((b) => b.status === 'open').length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="bugs-all-clear">No open bug reports — all clear.</p>
          )}
          {bugs.filter((b) => b.status === 'open').map((b) => renderBug(b))}
          {bugs.filter((b) => b.status !== 'open').length > 0 && (
            <div className="pt-1">
              <button onClick={() => setShowResolved((s) => !s)} data-testid="bugs-resolved-toggle"
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showResolved ? '' : '-rotate-90'}`} />
                Resolved ({bugs.filter((b) => b.status !== 'open').length})
              </button>
              {showResolved && (
                <div className="mt-2 space-y-2">
                  {bugs.filter((b) => b.status !== 'open').map((b) => renderBug(b))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {viewImg && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-8 cursor-zoom-out"
          onClick={() => setViewImg(null)} data-testid="bug-screenshot-modal">
          <img src={viewImg} alt="screenshot" className="max-w-full max-h-full rounded-sm border border-border" />
        </div>
      )}
    </div>
  );
}
